/**
 * FindingFlashCard
 *
 * Ephemeral in-turn finding annotation from SSE (display-only, no actions).
 *
 * @module @ideoz/ui/components/molecules/FindingFlashCard
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../atoms/badge';
import { cn } from '../../lib/utils';
import type { FindingFlashCardProps } from '../../types/Findings.types';

const TYPE_LABELS: Record<string, string> = {
  key_insight: 'Key insight',
  contradiction: 'Contradiction',
  assumption: 'Assumption',
};

/**
 * FindingFlashCard Component
 */
export const FindingFlashCard: React.FC<FindingFlashCardProps> = ({
  flash,
  className = '',
  testId = 'finding-flash-card',
}) => {
  const { t } = useTranslation();
  return (
    <article
      data-testid={testId}
      className={cn(
        'rounded-lg border border-dashed border-primary-200 bg-primary-25/40 p-3 text-sm',
        className
      )}
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <Badge variant="info" size="xs">
          {t(`finding.type.${flash.type}`, { defaultValue: TYPE_LABELS[flash.type] ?? flash.type })}
        </Badge>
        <Badge variant="secondary" size="xs">
          {t('scopeIntel.thisTurn')}
        </Badge>
        {flash.severity && (
          <Badge variant="warning" size="xs">
            {flash.severity}
          </Badge>
        )}
      </div>
      <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{flash.content}</p>
      {flash.type === 'contradiction' && (flash.side_a || flash.side_b) && (
        <div className="mt-2 space-y-1 text-xs text-gray-600">
          {flash.side_a && <p>A: {flash.side_a}</p>}
          {flash.side_b && <p>B: {flash.side_b}</p>}
        </div>
      )}
    </article>
  );
};

export default FindingFlashCard;
